/**
 * Generates a stable, non-cryptographic, short ID from a string input.
 *
 * This function is deterministic: the same input will always produce the same output.
 * It uses a simple hashing algorithm to convert the string into a base-36 number.
 *
 * @remarks
 * The generated ID is **not** cryptographically secure and should only be used for
 * purposes like generating stable DOM element IDs or unique keys for list rendering.
 *
 * @param input - The string to convert into a stable ID.
 * @param prefix - An optional prefix for namespacing the ID, separated by `::`.
 * @returns A deterministic base-36 encoded ID string.
 *
 * @example
 * ```
 * const id1 = getStableId('some-unique-content');
 * // => '1j3j4k' (example output)
 *
 * const id2 = getStableId('some-unique-content', 'user-card');
 * // => 'user-card::1j3j4k'
 *
 * // Stability check
 * const id3 = getStableId('some-unique-content');
 * // id3 is always equal to id1
 * ```
 */
export const getStableId = (input: string, prefix?: string): string => {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0; // Force to 32bit integer
  }
  return prefix ? `${prefix}::${Math.abs(h).toString(36)}` : `${Math.abs(h).toString(36)}`;
};
